import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    ArrowLeft, ArrowsClockwise, Briefcase, Buildings, FileText, Receipt,
    HandCoins, Repeat, ArrowsClockwise as Reverse, CaretRight, Coins,
    ShieldCheck, CheckCircle, Warning,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import RoleGuard from "../../components/RoleGuard";
import ProjectAssignmentsCard from "./ProjectAssignmentsCard";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";
import { STATE_TONE, STATE_LABEL } from "./constants";

export default function ProjectDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const { user, can } = useAuth();
    const [project, setProject] = useState(null);
    const [loading, setLoading] = useState(true);
    const [recomputing, setRecomputing] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/billing/project/${id}`);
            setProject(data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load project"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const recomputeSummary = async () => {
        setRecomputing(true);
        try {
            await api.post(`/billing/project/admin/recompute-summary/${id}`);
            toast.success("Commercial summary refreshed");
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Recompute failed"));
        } finally {
            setRecomputing(false);
        }
    };

    const detachAmc = async (amcId, amcName) => {
        const note = window.prompt(
            `Detach "${amcName}" from this project?\n\nThis unlinks the AMC from this project. Operator note (optional):`,
            "",
        );
        if (note === null) return; // user cancelled
        try {
            await api.post(`/billing/project/${id}/detach-amc`, {
                amc_schedule_id: amcId,
                note: note || undefined,
            });
            toast.success(`Detached ${amcName}`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err, "Detach failed"));
        }
    };

    if (loading && !project) {
        return (
            <div data-testid="project-detail-loading">
                <PageHeader eyebrow="Project" title="Loading project…" />
            </div>
        );
    }
    if (!project) return null;

    const cs = project.commercial_summary || {};
    const pos = project.pos || [];
    const invoices = project.invoices || [];
    const amcs = project.amc_schedules || [];
    const payments = project.payments || [];
    const creditNotes = project.credit_notes || [];

    return (
        <div data-testid="project-detail-page">
            <PageHeader
                eyebrow={
                    <button
                        onClick={() => nav("/projects")}
                        className="hover:text-indigo-600 transition-colors flex items-center gap-1"
                        data-testid="project-detail-back"
                    >
                        <ArrowLeft size={11} /> Projects
                    </button>
                }
                title={
                    <span className="flex items-center gap-3 flex-wrap">
                        <Briefcase size={22} weight="duotone" className="text-indigo-600" />
                        <span className="font-mono">{project.project_number}</span>
                    </span>
                }
                description={project.name && project.name !== project.project_number ? `Engagement name: ${project.name}` : "Universal engagement record — commercial + technical + support state."}
                actions={
                    <div className="flex items-center gap-2">
                        <RoleGuard permission="legacy_project:admin">
                            <Button
                                variant="outline" size="sm"
                                onClick={recomputeSummary}
                                disabled={recomputing}
                                className="gap-1.5"
                                data-testid="project-recompute-btn"
                            >
                                <Reverse size={13} className={recomputing ? "animate-spin" : ""} />
                                Recompute summary
                            </Button>
                        </RoleGuard>
                        <Button
                            variant="outline" size="sm"
                            onClick={load}
                            className="gap-1.5"
                            data-testid="project-refresh-btn"
                        >
                            <ArrowsClockwise size={13} /> Refresh
                        </Button>
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* Identity card — client + provenance */}
                <div className="reedos-card p-5" data-testid="project-identity-card">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-5">
                        <IdentityField
                            label="Client"
                            value={
                                project.client?.name
                                    ? (
                                        <Link
                                            to={`/clients/${project.client.id}/ledger`}
                                            className="text-indigo-700 hover:underline flex items-center gap-1.5"
                                        >
                                            <Buildings size={12} />
                                            {project.client.name}
                                        </Link>
                                    )
                                    : "—"
                            }
                            testid="project-field-client"
                        />
                        <IdentityField
                            label="Source proposal"
                            value={project.source_proposal_number || "—"}
                            testid="project-field-proposal"
                        />
                        <IdentityField
                            label="Linked POs"
                            value={(project.linked_po_numbers || []).join(", ") || "—"}
                            testid="project-field-pos"
                        />
                        <IdentityField
                            label="Created"
                            value={formatDate(project.created_at)}
                            testid="project-field-created"
                        />
                    </div>
                </div>

                {/* Triple-state strip */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="project-triple-state">
                    <StateCard
                        icon={<CheckCircle size={16} weight="duotone" />}
                        label="Technical state"
                        state={project.technical_state}
                        date={project.technical_completion_date}
                        hint="Delivery progress"
                        testid="state-technical"
                    />
                    <StateCard
                        icon={<Coins size={16} weight="duotone" />}
                        label="Commercial state"
                        state={project.commercial_state}
                        date={project.commercial_closure_date}
                        hint="Money flow"
                        testid="state-commercial"
                    />
                    <StateCard
                        icon={<ShieldCheck size={16} weight="duotone" />}
                        label="Support state"
                        state={project.support_state}
                        date={project.support_activation_date}
                        hint="AMC lifecycle"
                        testid="state-support"
                    />
                </div>

                {/* Commercial summary KPI strip */}
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="project-financials">
                    <MoneyTile label="PO total" value={cs.po_total} tone="slate" testid="money-po-total" />
                    <MoneyTile label="Invoiced" value={cs.invoiced_total} tone="indigo" testid="money-invoiced" />
                    <MoneyTile label="Received" value={cs.received_total} tone="emerald" testid="money-received" />
                    <MoneyTile
                        label="Outstanding"
                        value={cs.outstanding_total}
                        tone={Number(cs.outstanding_total) > 0 ? "rose" : "slate"}
                        testid="money-outstanding"
                    />
                    <MoneyTile label="Credit notes" value={cs.credit_note_total} tone="amber" testid="money-cn" />
                </div>
                {cs.last_computed_at && (
                    <div className="text-[10px] text-slate-400 -mt-3 px-1">
                        Last computed {formatDate(cs.last_computed_at)} · {new Date(cs.last_computed_at).toLocaleTimeString("en-IN")}
                    </div>
                )}

                {/* Iter 2026-02 · Cross-role assignments — Sales/Admin
                    picks Technical / Commercial / Support owners. Anyone
                    already assigned (self-editable team model) can also
                    edit. Newly-added owners get an email + see the
                    project on My Projects. */}
                <ProjectAssignmentsCard
                    project={project}
                    canAssign={
                        can?.("legacy_project:assign") ||
                        // Anyone already assigned can add/remove teammates
                        (project.assignments || {}) &&
                        ["technical", "commercial", "support"].some((r) =>
                            (project.assignments?.[r] || []).some((e) => e.user_id === user?.id)
                        )
                    }
                    onChange={(assignments) => setProject((p) => ({ ...p, assignments }))}
                />

                {/* Linked POs */}
                <SectionCard
                    icon={<FileText size={14} weight="duotone" className="text-indigo-500" />}
                    title="Linked Purchase Orders"
                    count={pos.length}
                    testid="section-pos"
                >
                    {pos.length === 0 ? (
                        <Empty label="No linked POs" />
                    ) : (
                        <Table cols={["PO number", "Source", "Client ref", "Status", ""]} testid="pos-table">
                            {pos.map((p) => (
                                <tr
                                    key={p.id}
                                    className="border-t border-slate-100 reedos-row-hover cursor-pointer"
                                    onClick={() => nav(`/purchase-orders/${p.id}`)}
                                    data-testid={`po-row-${p.id}`}
                                >
                                    <td className="px-4 py-2 text-xs font-mono">{p.po_number}</td>
                                    <td className="px-4 py-2 text-xs text-slate-600">{p.po_source_type || "—"}</td>
                                    <td className="px-4 py-2 text-xs">
                                        {p.client_po_number ? (
                                            <span className="font-mono text-slate-700">{p.client_po_number}</span>
                                        ) : <span className="text-slate-400">—</span>}
                                    </td>
                                    <td className="px-4 py-2 text-xs">
                                        <span className={classJoin(
                                            "inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold border",
                                            p.is_final ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200",
                                        )}>
                                            {p.is_final ? "Finalized" : "Draft"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 w-6"><CaretRight size={12} className="text-slate-300" /></td>
                                </tr>
                            ))}
                        </Table>
                    )}
                </SectionCard>

                {/* Invoices */}
                <SectionCard
                    icon={<Receipt size={14} weight="duotone" className="text-amber-500" />}
                    title="Invoices"
                    count={invoices.length}
                    testid="section-invoices"
                >
                    {invoices.length === 0 ? (
                        <Empty label="No invoices yet" />
                    ) : (
                        <Table cols={["Number", "Invoice date", "Due date", "Status", "Amount", ""]} testid="invoices-table">
                            {invoices.map((inv) => (
                                <tr
                                    key={inv.id}
                                    className="border-t border-slate-100 reedos-row-hover cursor-pointer"
                                    onClick={() => nav(`/invoices/${inv.id}`)}
                                    data-testid={`invoice-row-${inv.id}`}
                                >
                                    <td className="px-4 py-2 text-xs font-mono">{inv.invoice_number}</td>
                                    <td className="px-4 py-2 text-xs">{formatDate(inv.invoice_date)}</td>
                                    <td className="px-4 py-2 text-xs">{formatDate(inv.due_date)}</td>
                                    <td className="px-4 py-2 text-xs">
                                        <span className="inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold border bg-slate-50 text-slate-700 border-slate-200">
                                            {inv.status}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-xs text-right font-mono">{formatINR(inv.total_amount)}</td>
                                    <td className="px-4 py-2 w-6"><CaretRight size={12} className="text-slate-300" /></td>
                                </tr>
                            ))}
                        </Table>
                    )}
                </SectionCard>

                {/* AMC schedules — w/ detach action for adopted ones */}
                <SectionCard
                    icon={<Repeat size={14} weight="duotone" className="text-violet-500" />}
                    title="AMC schedules"
                    count={amcs.length}
                    testid="section-amcs"
                >
                    {amcs.length === 0 ? (
                        <Empty
                            label="No AMC schedules linked"
                        />
                    ) : (
                        <Table cols={["AMC", "Frequency", "Start", "Next due", "Status", "Amount", ""]} testid="amcs-table">
                            {amcs.map((a) => (
                                <tr
                                    key={a.id}
                                    className="border-t border-slate-100 reedos-row-hover"
                                    data-testid={`amc-row-${a.id}`}
                                >
                                    <td className="px-4 py-2 text-xs">
                                        <Link to={`/amc`} className="font-medium text-slate-800 hover:text-indigo-700">
                                            {a.amc_name || "—"}
                                        </Link>
                                    </td>
                                    <td className="px-4 py-2 text-xs text-slate-600">{a.billing_frequency || "—"}</td>
                                    <td className="px-4 py-2 text-xs">{formatDate(a.start_date)}</td>
                                    <td className="px-4 py-2 text-xs">{formatDate(a.next_due_date)}</td>
                                    <td className="px-4 py-2 text-xs">
                                        <span className={classJoin(
                                            "inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold border",
                                            a.status === "Active" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                                : a.status === "Paused" ? "bg-amber-50 text-amber-700 border-amber-200"
                                                : "bg-slate-50 text-slate-600 border-slate-200",
                                        )}>
                                            {a.status || "—"}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2 text-xs text-right font-mono">{formatINR(a.amount)}</td>
                                    <td className="px-4 py-2 w-20">
                                        <RoleGuard permission="legacy_project:admin">
                                            <button
                                                onClick={() => detachAmc(a.id, a.amc_name || "this AMC")}
                                                className="text-[10px] text-rose-600 hover:text-rose-800 font-semibold"
                                                data-testid={`amc-detach-${a.id}`}
                                                title="Detach AMC from this project"
                                            >
                                                Detach
                                            </button>
                                        </RoleGuard>
                                    </td>
                                </tr>
                            ))}
                        </Table>
                    )}
                </SectionCard>

                {/* Payments */}
                <SectionCard
                    icon={<HandCoins size={14} weight="duotone" className="text-emerald-500" />}
                    title="Payments"
                    count={payments.length}
                    testid="section-payments"
                >
                    {payments.length === 0 ? (
                        <Empty label="No payments yet" />
                    ) : (
                        <Table cols={["Date", "Invoice", "Mode", "Amount"]} testid="payments-table">
                            {payments.map((p) => (
                                <tr key={p.id} className="border-t border-slate-100" data-testid={`payment-row-${p.id}`}>
                                    <td className="px-4 py-2 text-xs">{formatDate(p.payment_date)}</td>
                                    <td className="px-4 py-2 text-xs font-mono text-slate-600">
                                        <Link to={`/invoices/${p.invoice_id}`} className="hover:text-indigo-700">{p.invoice_id}</Link>
                                    </td>
                                    <td className="px-4 py-2 text-xs">{p.mode || "—"}</td>
                                    <td className="px-4 py-2 text-xs text-right font-mono">{formatINR(p.amount)}</td>
                                </tr>
                            ))}
                        </Table>
                    )}
                </SectionCard>

                {/* Credit notes */}
                {creditNotes.length > 0 && (
                    <SectionCard
                        icon={<Receipt size={14} weight="duotone" className="text-rose-500" />}
                        title="Credit notes"
                        count={creditNotes.length}
                        testid="section-credit-notes"
                    >
                        <Table cols={["CN number", "Date", "Invoice", "Amount"]} testid="cn-table">
                            {creditNotes.map((cn) => (
                                <tr key={cn.id} className="border-t border-slate-100" data-testid={`cn-row-${cn.id}`}>
                                    <td className="px-4 py-2 text-xs font-mono">{cn.cn_number}</td>
                                    <td className="px-4 py-2 text-xs">{formatDate(cn.cn_date)}</td>
                                    <td className="px-4 py-2 text-xs font-mono text-slate-600">
                                        <Link to={`/invoices/${cn.invoice_id}`} className="hover:text-indigo-700">{cn.invoice_id}</Link>
                                    </td>
                                    <td className="px-4 py-2 text-xs text-right font-mono">{formatINR(cn.amount)}</td>
                                </tr>
                            ))}
                        </Table>
                    </SectionCard>
                )}

                {/* Override log — Phase 2 prep */}
                {(project.overrides || []).length > 0 && (
                    <SectionCard
                        icon={<Warning size={14} weight="duotone" className="text-amber-500" />}
                        title="Override log"
                        count={(project.overrides || []).length}
                        testid="section-overrides"
                    >
                        <div className="px-4 py-3 space-y-2">
                            {project.overrides.map((o, i) => (
                                <div key={i} className="text-xs text-slate-700 border-l-2 border-amber-300 pl-3 py-1" data-testid={`override-${i}`}>
                                    <div className="font-mono text-[10px] text-slate-500">{formatDate(o.at)} · {o.by?.email || o.by?.id || "system"}</div>
                                    <div className="mt-0.5">{o.note || o.reason || "—"}</div>
                                </div>
                            ))}
                        </div>
                    </SectionCard>
                )}

                {/* Footer — Phase 2 hint */}
                <div className="reedos-card p-4 border-l-4 border-l-indigo-300 bg-indigo-50/40" data-testid="project-phase2-hint">
                    <div className="flex items-start gap-3">
                        <Warning size={16} className="text-indigo-600 mt-0.5 shrink-0" />
                        <div className="text-xs text-slate-700">
                            <div className="font-semibold mb-1">Coming in Phase 2</div>
                            <p className="text-slate-600">
                                Mark Project as Completed · Activate AMC rule · Commercial closure · Override capture.
                                For now, use the Adopt-AMC affordance above to retroactively link manual AMCs.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function IdentityField({ label, value, testid }) {
    return (
        <div data-testid={testid}>
            <div className="reedos-eyebrow text-[9px]">{label}</div>
            <div className="font-medium text-sm text-slate-900 mt-1 break-words">{value}</div>
        </div>
    );
}

function MoneyTile({ label, value, tone = "slate", testid }) {
    const toneText = {
        slate: "text-slate-900",
        indigo: "text-indigo-700",
        emerald: "text-emerald-700",
        amber: "text-amber-700",
        rose: "text-rose-700",
        violet: "text-violet-700",
    }[tone] || "text-slate-900";
    return (
        <div className="reedos-card p-3 border border-slate-200" data-testid={testid}>
            <div className="reedos-eyebrow text-[9px]">{label}</div>
            <div className={classJoin("font-display font-bold reedos-data text-xl mt-1 leading-none", toneText)}>
                {formatINR(value)}
            </div>
        </div>
    );
}

function StateCard({ icon, label, state, date, hint, testid }) {
    return (
        <div className="reedos-card p-4" data-testid={testid}>
            <div className="reedos-eyebrow text-[9px] flex items-center gap-1.5">{icon} {label}</div>
            <div className="mt-2">
                <span className={classJoin(
                    "inline-flex items-center rounded px-2.5 py-1 text-xs font-semibold border",
                    STATE_TONE[state] || "bg-slate-50 text-slate-600 border-slate-200",
                )}>
                    {STATE_LABEL[state] || state || "—"}
                </span>
            </div>
            <div className="text-[10px] text-slate-500 mt-2">{hint}</div>
            {date && <div className="text-[10px] text-slate-400 mt-0.5">{formatDate(date)}</div>}
        </div>
    );
}

function SectionCard({ icon, title, count, children, testid, headerAction }) {
    return (
        <div className="reedos-card overflow-hidden" data-testid={testid}>
            <div className="px-4 py-2.5 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                    {icon}
                    <span>{title}</span>
                    {count != null && (
                        <span className="text-[10px] font-mono text-slate-500 bg-white px-1.5 py-0.5 rounded border border-slate-200">
                            {count}
                        </span>
                    )}
                </div>
                {headerAction}
            </div>
            {children}
        </div>
    );
}

function Empty({ label, cta }) {
    return (
        <div className="px-4 py-6 text-center text-xs text-slate-500">
            {label}
            {cta && <div className="mt-3">{cta}</div>}
        </div>
    );
}

function Table({ cols, children, testid }) {
    return (
        <div className="overflow-x-auto" data-testid={testid}>
            <table className="w-full text-sm">
                <thead className="bg-white border-b border-slate-200">
                    <tr className="text-left">
                        {cols.map((c, i) => (
                            <th key={i} className={classJoin(
                                "px-4 py-2 reedos-eyebrow text-[9px]",
                                typeof c === "string" && (c.includes("Amount") || c.includes("amount")) ? "text-right" : "",
                            )}>
                                {c}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>{children}</tbody>
            </table>
        </div>
    );
}
